import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItem,
  Menu,
  MenuItem,
  Skeleton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DriveFileMoveOutlinedIcon from '@mui/icons-material/DriveFileMoveOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { addScopeTodo, assignScopeTodo, getScopeTodos, updateScopeTodo } from '../../utils/shapeUpApi';
import AssigneePicker, { matchAssignee } from './AssigneePicker';
import AttachmentStrip from './AttachmentStrip';
import {
  ACCEPTED_IMAGE_TYPES,
  MAX_IMAGE_BYTES,
  deleteAttachment,
  describeUploadError,
  pastedImageName,
  prepareImage,
  uploadTodoAttachment,
} from '../../utils/attachmentsApi';

const MAX_IMAGES_PER_TODO = 5;

/**
 * A scope's todo list, which is its Jira issue's subtasks. Nothing is stored
 * locally, so checking a box transitions the subtask in Jira and a rename
 * edits the subtask summary.
 */
const ScopeTodoList = ({
  scope,
  manual = false,
  readOnly = false,
  attachmentsEnabled = false,
  preloadedTodos = null,
  showHeading = true,
  onTodosChanged,
  onDragTodo,
  onDropTodo,
  onReturnToScratchpad,
  moveTargets = [],
  movingKey = null,
  members = [],
}) => {
  const [moveMenu, setMoveMenu] = useState(null);
  const [todos, setTodos] = useState(preloadedTodos || []);
  const [loading, setLoading] = useState(!preloadedTodos);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState('');
  const [adding, setAdding] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [editingKey, setEditingKey] = useState(null);
  const [editDraft, setEditDraft] = useState('');
  const [uploadingKey, setUploadingKey] = useState(null);
  const [deletingAttachmentId, setDeletingAttachmentId] = useState(null);
  // Which todo the hidden file input is currently picking for, since one input
  // is shared by every row rather than one per row.
  const pickingFor = useRef(null);
  const fileInputRef = useRef(null);

  const scopeId = scope?.id;

  // Only a manual todo can hold our images. With Jira on, a todo is a subtask
  // and its attachments belong to Jira, which is where they are shown.
  const canAttach = manual && !readOnly && attachmentsEnabled;

  const load = useCallback(async () => {
    if (!scopeId) return;
    setLoading(true);
    try {
      const data = await getScopeTodos(scopeId);
      setTodos(data.todos || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.message || (manual ? 'Could not load todos.' : 'Could not load todos from Jira.'));
    } finally {
      setLoading(false);
    }
  }, [scopeId, manual]);

  // The board already fetched every scope's subtasks in one request, so a row
  // given those does not go back to Jira for its own copy.
  useEffect(() => {
    if (preloadedTodos) {
      setTodos(preloadedTodos);
      setLoading(false);
      return;
    }
    load();
  }, [load, preloadedTodos]);

  // Whoever owns the surrounding list keeps its own counts, so every change
  // here is announced rather than left to be discovered on the next reload.
  const commit = (next) => {
    setTodos(next);
    if (onTodosChanged) onTodosChanged(next);
  };

  const replaceTodo = (updated) => {
    commit(todos.map(todo => (todo.key === updated.key ? updated : todo)));
  };

  const handleAdd = async () => {
    const summary = draft.trim();
    if (!summary) return;
    setAdding(true);
    setError(null);
    try {
      const created = await addScopeTodo(scopeId, summary);
      commit([...todos, created]);
      setDraft('');
    } catch (err) {
      setError(err.response?.data?.message || (manual ? 'Could not add the todo.' : 'Jira would not accept the new subtask.'));
    } finally {
      setAdding(false);
    }
  };

  const handleToggle = async (todo) => {
    setBusyKey(todo.key);
    setError(null);
    try {
      replaceTodo({ ...todo, done: !todo.done });
      const updated = await updateScopeTodo(scopeId, todo.key, { done: !todo.done });
      replaceTodo(updated);
    } catch (err) {
      replaceTodo(todo);
      setError(err.response?.data?.message || (manual ? `Could not update ${todo.summary}.` : `Could not update ${todo.key} in Jira.`));
    } finally {
      setBusyKey(null);
    }
  };

  const handleRename = async (todo) => {
    const summary = editDraft.trim();
    if (!summary || summary === todo.summary) {
      setEditingKey(null);
      return;
    }
    setBusyKey(todo.key);
    setError(null);
    try {
      const updated = await updateScopeTodo(scopeId, todo.key, { summary });
      replaceTodo(updated);
      setEditingKey(null);
    } catch (err) {
      setError(err.response?.data?.message || (manual ? `Could not rename ${todo.summary}.` : `Could not rename ${todo.key} in Jira.`));
    } finally {
      setBusyKey(null);
    }
  };

  const handleAssign = async (todo, userId) => {
    setBusyKey(todo.key);
    setError(null);
    try {
      const updated = await assignScopeTodo(scopeId, todo.key, userId);
      replaceTodo(updated);
    } catch (err) {
      replaceTodo(todo);
      setError(err.response?.data?.message || (manual ? `Could not assign ${todo.summary}.` : `Could not assign ${todo.key} in Jira.`));
    } finally {
      setBusyKey(null);
    }
  };

  /**
   * Attach images to a manual todo, one at a time. Sequential because each is
   * downscaled on a canvas first, and doing several at once on large
   * screenshots is what makes the tab stutter.
   */
  const uploadToTodo = async (todo, files) => {
    setUploadingKey(todo.key);
    setError(null);

    let lastError = null;
    let added = [];
    for (const file of files) {
      try {
        const named = file.name
          ? file
          : new File([file], pastedImageName(file.type), { type: file.type });
        const { file: prepared, width, height } = await prepareImage(named);

        if (prepared.size > MAX_IMAGE_BYTES) {
          lastError = 'That image is too large, even after resizing.';
          continue;
        }

        const created = await uploadTodoAttachment(todo.key, prepared, { width, height });
        added = [...added, created];
      } catch (err) {
        lastError = describeUploadError(err);
      }
    }

    if (added.length) {
      replaceTodo({ ...todo, attachments: [...(todo.attachments || []), ...added] });
    }
    setUploadingKey(null);
    if (lastError) setError(lastError);
  };

  const handlePickFiles = (event) => {
    const files = Array.from(event.target.files || []);
    // Reset so picking the same file twice in a row still fires a change event.
    event.target.value = '';
    const todo = pickingFor.current;
    pickingFor.current = null;
    if (files.length && todo) uploadToTodo(todo, files);
  };

  const handleDeleteAttachment = async (todo, attachment) => {
    setDeletingAttachmentId(attachment.id);
    setError(null);
    try {
      await deleteAttachment(attachment.id);
      replaceTodo({
        ...todo,
        attachments: (todo.attachments || []).filter(a => a.id !== attachment.id),
      });
    } catch (err) {
      setError(describeUploadError(err));
    } finally {
      setDeletingAttachmentId(null);
    }
  };

  const doneCount = todos.filter(todo => todo.done).length;
  const canAssign = !readOnly && members.length > 0;
  const canDrag = !readOnly && Boolean(onDragTodo);
  // Dragging needs a mouse and a visible target, so the same move is also a
  // menu: on a long bet the other scope is usually off screen.
  const canMove = !readOnly && ((Boolean(onDropTodo) && moveTargets.length > 0) || Boolean(onReturnToScratchpad));

  return (
    <Box>
      {showHeading && (
        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Todos</Typography>
          <Typography variant="caption" color="text.secondary">
            {(() => {
              const label = scope.jiraKey ? `Jira subtasks of ${scope.jiraKey}` : 'What is left to finish this scope';
              return todos.length > 0 ? `${doneCount} of ${todos.length} done · ${label}` : label;
            })()}
          </Typography>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* One input for the whole list rather than one per row; the row that
          opened it is remembered in pickingFor. */}
      {canAttach && (
        <input
          ref={fileInputRef}
          type="file"
          accept={ACCEPTED_IMAGE_TYPES.join(',')}
          hidden
          onChange={handlePickFiles}
        />
      )}

      {loading ? (
        <Skeleton variant="rectangular" height={72} sx={{ borderRadius: 1 }} />
      ) : (
        <List dense disablePadding>
          {todos.map(todo => (
            <ListItem
              key={todo.key}
              disableGutters
              // Text selection inside the rename field would otherwise start a
              // drag instead.
              draggable={canDrag && editingKey !== todo.key}
              onDragStart={(event) => {
                event.dataTransfer.effectAllowed = 'move';
                event.dataTransfer.setData('text/plain', todo.key);
                onDragTodo(todo);
              }}
              onDragEnd={() => onDragTodo(null)}
              sx={{
                gap: 1,
                alignItems: 'center',
                opacity: movingKey === todo.key ? 0.4 : 1,
                cursor: canDrag && editingKey !== todo.key ? 'grab' : 'default',
              }}
              secondaryAction={
                !readOnly && editingKey !== todo.key ? (
                  <Box sx={{ display: 'flex' }}>
                    {canMove && (
                      <Tooltip title="Move to another scope">
                        <span>
                          <IconButton
                            size="small"
                            disabled={busyKey === todo.key || movingKey === todo.key}
                            onClick={(event) => setMoveMenu({ el: event.currentTarget, todo })}
                          >
                            <DriveFileMoveOutlinedIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    {canAttach && (
                      <Tooltip title={(todo.attachments || []).length >= MAX_IMAGES_PER_TODO
                        ? `A todo can hold ${MAX_IMAGES_PER_TODO} images`
                        : 'Attach an image'}>
                        <span>
                          <IconButton
                            size="small"
                            aria-label="Attach image"
                            disabled={
                              uploadingKey === todo.key
                              || (todo.attachments || []).length >= MAX_IMAGES_PER_TODO
                            }
                            onClick={() => {
                              pickingFor.current = todo;
                              fileInputRef.current?.click();
                            }}
                          >
                            {uploadingKey === todo.key
                              ? <CircularProgress size={16} />
                              : <ImageOutlinedIcon fontSize="small" />}
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                    <Tooltip title={manual ? 'Rename' : 'Rename in Jira'}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={busyKey === todo.key}
                          onClick={() => { setEditingKey(todo.key); setEditDraft(todo.summary); }}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                ) : null
              }
            >
              {canDrag && (
                <DragIndicatorIcon sx={{ fontSize: 16, color: 'text.disabled', flexShrink: 0 }} />
              )}

              <Checkbox
                edge="start"
                size="small"
                checked={todo.done}
                disabled={readOnly || busyKey === todo.key || movingKey === todo.key}
                onChange={() => handleToggle(todo)}
                sx={{ p: 0.5 }}
              />

              {editingKey === todo.key ? (
                <TextField
                  size="small"
                  fullWidth
                  autoFocus
                  value={editDraft}
                  onChange={(e) => setEditDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleRename(todo);
                    if (e.key === 'Escape') setEditingKey(null);
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => handleRename(todo)} disabled={busyKey === todo.key}>
                          <CheckIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => setEditingKey(null)}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              ) : (
                <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        textDecoration: todo.done ? 'line-through' : 'none',
                        color: todo.done ? 'text.disabled' : 'text.primary',
                        minWidth: 0,
                      }}
                    >
                      {todo.summary}
                    </Typography>
                    {todo.url && (
                      <Link
                        href={todo.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        variant="caption"
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.3, flexShrink: 0 }}
                      >
                        {todo.key}
                        <OpenInNewIcon sx={{ fontSize: 12 }} />
                      </Link>
                    )}
                    {busyKey === todo.key && <CircularProgress size={12} />}
                  </Box>

                  <AttachmentStrip
                    attachments={todo.attachments || []}
                    canEdit={canAttach}
                    onDelete={(attachment) => handleDeleteAttachment(todo, attachment)}
                    deletingId={deletingAttachmentId}
                  />
                </Box>
              )}

              {/* The owner sits before the row's action buttons. Editable users
                  get the picker; a reviewer sees a read-only avatar (or nothing
                  when unassigned). Hidden while renaming, where the row is text. */}
              {editingKey !== todo.key && (canAssign || matchAssignee(todo, members) || todo.assigneeName) && (
                <Box sx={{ flexShrink: 0, mr: readOnly ? 0 : 6 }}>
                  <AssigneePicker
                    target={todo}
                    members={members}
                    onAssign={canAssign ? (userId) => handleAssign(todo, userId) : undefined}
                    disabled={busyKey === todo.key || movingKey === todo.key}
                    size={22}
                  />
                </Box>
              )}
            </ListItem>
          ))}

          {todos.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              No subtasks yet.
            </Typography>
          )}
        </List>
      )}

      <Menu
        anchorEl={moveMenu?.el || null}
        open={Boolean(moveMenu)}
        onClose={() => setMoveMenu(null)}
      >
        {moveTargets.map(target => (
          <MenuItem
            key={target.id}
            onClick={() => {
              const todo = moveMenu.todo;
              setMoveMenu(null);
              onDropTodo(todo, target.id);
            }}
          >
            {target.label}
          </MenuItem>
        ))}
        {onReturnToScratchpad && moveTargets.length > 0 && <Divider />}
        {onReturnToScratchpad && (
          <MenuItem
            onClick={() => {
              const todo = moveMenu.todo;
              setMoveMenu(null);
              onReturnToScratchpad(todo);
            }}
          >
            Return to scratchpad
          </MenuItem>
        )}
      </Menu>

      {!readOnly && (
        <TextField
          size="small"
          fullWidth
          sx={{ mt: 1 }}
          placeholder={manual ? 'Add a todo' : 'Add a todo, which becomes a Jira subtask'}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleAdd(); }}
          disabled={adding}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={handleAdd} disabled={adding || !draft.trim()}>
                  {adding ? <CircularProgress size={16} /> : <AddIcon fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      )}
    </Box>
  );
};

export default ScopeTodoList;
